import { z } from "zod";

/**
 * Phone number validation regex
 * Allows:
 * - Optional + at the start (international format)
 * - Digits, spaces, dashes, parentheses, and forward slashes
 * - Minimum 7 digits (after removing non-digit characters)
 * - Maximum 50 characters total
 */
const PHONE_REGEX = /^[+]?[\d\s\-()/]+$/;

/**
 * Validates a phone number string
 * @param phone - Phone number string to validate
 * @returns true if valid, false otherwise
 */
export function isValidPhoneNumber(phone: string): boolean {
  if (!phone || phone.trim().length === 0) {
    return true; // Empty is valid (optional field)
  }

  // Check basic format
  if (!PHONE_REGEX.test(phone)) {
    return false;
  }

  // Count digits (minimum 7 required)
  const digitCount = phone.replace(/\D/g, "").length;
  if (digitCount < 7) {
    return false;
  }

  // Check maximum length
  if (phone.length > 50) {
    return false;
  }

  return true;
}

/**
 * Zod schema for optional phone number validation
 * Validates phone format if provided, but allows empty string
 */
export const phoneSchema = z
  .string()
  .max(50, "Phone number must not exceed 50 characters")
  .optional()
  .or(z.literal(""))
  .refine(
    (val) => {
      if (!val || val.trim().length === 0) {
        return true; // Empty is valid
      }
      return isValidPhoneNumber(val);
    },
    {
      message: "Please enter a valid phone number (minimum 7 digits, may include +, spaces, dashes, parentheses)",
    }
  );
